import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { GoogleSignInButton } from '../../components/GoogleSignInButton';
import { googleAuthError } from '../../utils/googleAuth';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

export default function LoginPage({ oauthError }) {
  const { language, t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      // Small delay for state propagation
      await new Promise((resolve) => setTimeout(resolve, 100));

      if (oauthError === 'google_link_required') {
        navigate('/profile', { replace: true });
      } else if (user.onboarding_completed) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/onboarding', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Login failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="pp-auth min-h-screen bg-pp-bg">
      <Header />
      
      <main className="pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="pp-container">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-10">
              <h1 className="pp-heading-2 text-pp-text" data-testid="login-title">
                {t('auth.login')}
              </h1>
            </div>

            <div className="pp-card">
              {/* Google Login */}
              {oauthError && (
                <p role="alert" className="mb-5 text-sm text-pp-error" data-testid="google-login-feedback">
                  {googleAuthError(oauthError, language)}
                </p>
              )}
              <GoogleSignInButton testId="google-login-button" className="mb-8" />

              <div className="relative mb-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-pp-border" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-4 text-pp-text-muted uppercase tracking-wider">
                    {t('auth.orContinueWith')}
                  </span>
                </div>
              </div>

              {/* Email Login Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="p-4 rounded-xl bg-pp-error-muted text-pp-error text-sm" data-testid="login-error">
                    {error}
                  </div>
                )}

                <div>
                  <Label htmlFor="email" className="pp-label">
                    {t('auth.email')}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pp-input"
                    placeholder="you@example.com"
                    required
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label htmlFor="password" className="pp-label !mb-0">
                      {t('auth.password')}
                    </Label>
                    <Link 
                      to="/forgot-password" 
                      className="text-sm text-pp-navy hover:underline"
                      data-testid="login-forgot-password-link"
                    >
                      {t('auth.forgotPassword')}
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pr-12 pp-input"
                      placeholder="••••••••"
                      required
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-pp-text-muted hover:text-pp-text transition-colors"
                      data-testid="login-password-visibility-toggle"
                      aria-label={language === 'fr' ? 'Afficher ou masquer le mot de passe' : 'Show or hide password'}
                    >
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="pp-btn-primary w-full"
                  data-testid="login-submit-button"
                >
                  {loading ? t('common.loading') : t('auth.loginLink')}
                </button>
              </form>

              <p className="text-center text-pp-text-secondary mt-8">
                {t('auth.noAccount')}{' '}
                <Link to="/signup" className="text-pp-navy font-medium hover:underline" data-testid="login-signup-link">
                  {t('auth.signupLink')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
